import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { canManageToken } from '../features/token/token-page.component';
import { WalletManager } from '../managers/wallet.manager';
import { Kcc20MetadataApiService } from '../services/metadata/kcc20-metadata-api.service';
import {
  Kcc20TokenApiService,
  Kcc20TokenDetail,
} from '../services/tokens/kcc20-token-api.service';

const COVENANT_ID_HEX = /^[0-9a-f]{64}$/;

export const tokenManageRouteGuard: CanActivateFn = async (
  route,
): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const walletManager = inject(WalletManager);
  const tokenApi = inject(Kcc20TokenApiService);
  const metadataApi = inject(Kcc20MetadataApiService);

  // Mock builds have no real wallet session — keep the console browsable.
  if (environment.useMockData) {
    return true;
  }

  const covenantId = (route.paramMap.get('covenantId') || '').toLowerCase();
  if (!COVENANT_ID_HEX.test(covenantId)) {
    return router.parseUrl('/markets');
  }
  const tokenUrl = `/tokens/${covenantId}`;

  await walletManager.awaitSessionRestore();

  const walletOwner = walletManager.signedWalletSignal()?.kcc20Owner;
  if (!walletOwner) {
    return router.parseUrl(tokenUrl);
  }

  const [detail, permission] = await Promise.all([
    loadTokenDetail(tokenApi, covenantId),
    loadManagePermission(metadataApi, covenantId),
  ]);

  return canManageToken(detail, walletOwner, permission)
    ? true
    : router.parseUrl(tokenUrl);
};

async function loadTokenDetail(
  tokenApi: Kcc20TokenApiService,
  covenantId: string,
): Promise<Kcc20TokenDetail | undefined> {
  try {
    return await firstValueFrom(tokenApi.getTokenIdentity(covenantId));
  } catch {
    try {
      return await firstValueFrom(tokenApi.getToken(covenantId));
    } catch {
      return undefined;
    }
  }
}

async function loadManagePermission(
  metadataApi: Kcc20MetadataApiService,
  covenantId: string,
) {
  try {
    return await firstValueFrom(metadataApi.getPermissions(covenantId));
  } catch (error) {
    if (
      error instanceof HttpErrorResponse &&
      [401, 403, 404].includes(error.status)
    ) {
      return undefined;
    }
    return undefined;
  }
}
